import React from 'react';

import {
  IconCircleCheck,
  IconCircleDashed,
  IconClockHour4,
  IconLoader2,
} from '@tabler/icons-react';

import { ProgressIndicator } from 'ui-kit';

import { TAPESTRY_STAGE_NODES, TapestryStageNodeKey } from './stages';

export type PipelineStageDisplayStatus = 'pending' | 'in_progress' | 'completed' | 'awaiting';

export interface PipelineStageView {
  key: TapestryStageNodeKey;
  label: string;
  status: PipelineStageDisplayStatus;
}

// Minimal structural shape of the data the stepper reads, so the view-model is
// unit-testable with plain objects (the generated TapestryJobStatus type is
// structurally assignable to this).
interface StageNodeLike {
  status?: string | null;
  ready_for_review?: boolean | null;
}
export interface PipelineJobLike {
  overall_progress: number;
  decomposition?: StageNodeLike | null;
  classification?: StageNodeLike | null;
  detection?: StageNodeLike | null;
  extraction?: StageNodeLike | null;
  validation?: StageNodeLike | null;
  persistence?: StageNodeLike | null;
  review?: StageNodeLike | null;
}

// The api emits 'pending' | 'in_progress' | 'completed'; anything unexpected
// (including an empty/absent node) collapses to 'pending' so the stepper never
// renders an undefined state.
function normalizeNodeStatus(
  raw: string | null | undefined,
): Exclude<PipelineStageDisplayStatus, 'awaiting'> {
  if (raw === 'in_progress' || raw === 'completed') {
    return raw;
  }
  return 'pending';
}

/**
 * Pure mapping from a job's seven `TapestryStageStatus` nodes to the ordered
 * stepper rows. The review node surfaces as `awaiting` (not `in_progress`) when
 * it is the human review gate (`ready_for_review`), so the UI can say "awaiting
 * you" rather than implying the system is still working.
 */
export function buildPipelineStageViews(job: PipelineJobLike): PipelineStageView[] {
  return TAPESTRY_STAGE_NODES.map(({ key, label }) => {
    const node = job[key];
    const awaiting = key === 'review' && Boolean(node?.ready_for_review);
    const status: PipelineStageDisplayStatus = awaiting
      ? 'awaiting'
      : normalizeNodeStatus(node?.status);
    return { key, label, status };
  });
}

function StageIcon({ status }: { status: PipelineStageDisplayStatus }): JSX.Element {
  switch (status) {
    case 'completed':
      return <IconCircleCheck className="text-green-600" size={20} aria-hidden />;
    case 'in_progress':
      return <IconLoader2 className="animate-spin text-accent-emphasis" size={20} aria-hidden />;
    case 'awaiting':
      return <IconClockHour4 className="text-accent-emphasis" size={20} aria-hidden />;
    case 'pending':
    default:
      return <IconCircleDashed className="text-slate-400" size={20} aria-hidden />;
  }
}

export interface PipelineStatusProps {
  job: PipelineJobLike;
}

/**
 * Live view of the Tapestry pipeline: a single overall progress bar plus the
 * seven ordered stage nodes. Rendered while the run is in progress
 * (completion_outcome === 'in_progress'); terminal/gate UIs are separate
 * components (ENG-2792/2793/2794).
 */
export const PipelineStatus: React.FC<PipelineStatusProps> = ({ job }) => {
  const stages = buildPipelineStageViews(job);

  return (
    <div
      data-testid="tapestry-pipeline-status"
      className="flex flex-col gap-4 rounded-[8px] border border-slate-200 p-4"
    >
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-m font-bold">Processing your tape</span>
          <span className="text-sm text-slate-500">{job.overall_progress}%</span>
        </div>
        <ProgressIndicator percent={job.overall_progress} />
      </div>

      <ol className="flex flex-col gap-2">
        {stages.map((stage) => (
          <li
            key={stage.key}
            data-testid={`tapestry-stage-${stage.key}`}
            data-status={stage.status}
            className="flex items-center gap-2"
          >
            <StageIcon status={stage.status} />
            <span className="text-sm">{stage.label}</span>
            {stage.status === 'awaiting' && (
              <span className="text-sm text-accent-emphasis">— awaiting you</span>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};
